import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Description as DocumentIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import Breadcrumbs from 'components/common/Breadcrumbs';
import useDocuments from 'hooks/documents/useDocuments';
import {  DocumentType } from 'types/document';
import { useTerminology } from 'hooks/useTerminology';



function Documents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { documents, meta, isLoading, isError, downloadDocument } = useDocuments();
  const terminology = useTerminology();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page[number]') || '1'));

  const handleSearch = () => {
    const newParams = new URLSearchParams();
    if (searchQuery) newParams.set('q', searchQuery);
    newParams.set('page[number]', '1');
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSearchParams({});
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page[number]', page.toString());
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const getDocumentIcon = (type: DocumentType | string) => {
    switch (type) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'xls':
      case 'xlsx':
        return '📊';
      case 'ppt':
      case 'pptx':
        return '📽️';
      case 'image':
        return '🖼️';
      default:
        return '📄';
    }
  };

  // Documents are already filtered by the hook based on search params
  const filteredDocuments = documents || [];

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Breadcrumbs data={[{ label: 'Home', to: '/' }, { label: 'Documents' }]} />
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load documents. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs data={[{ label: 'Home', to: '/' }, { label: 'Documents' }]} />
      
      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Documents
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Search and download documents, manuals, and resources.
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3, boxShadow: 1 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 300 }}>
              <TextField
                fullWidth
                placeholder="Search documents by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton 
                        onClick={() => setSearchQuery('')} 
                        size="small"
                        sx={{ 
                          color: 'text.secondary',
                          '&:hover': { color: 'text.primary' }
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ 
                  minWidth: 100,
                  height: 40,
                  borderRadius: 2,
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                sx={{ 
                  minWidth: 100,
                  height: 40,
                  borderRadius: 2,
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {meta.count} document{meta.count !== 1 ? 's' : ''} found
            </Typography>
          </Box>

          {filteredDocuments.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No documents found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search criteria or filters.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <>
              <Grid container spacing={3}>
                {filteredDocuments.map((document) => (
                  <Grid item xs={12} sm={6} md={4} key={document.id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" component="span" sx={{ mr: 1 }}>
                            {getDocumentIcon(document.fileType)}
                          </Typography>
                          <Typography variant="h6" component="h3" noWrap>
                            {document.name}
                          </Typography>
                        </Box>
                        
                        {document.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {document.description}
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                          <Chip
                            label={document.fileType ? document.fileType.toUpperCase() : 'PDF'}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {document.pickup?.orderNumber && (
                            <Chip
                              label={`${terminology.Pickup}: ${document.pickup.orderNumber}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary">
                          {document.fileType ? document.fileType.toUpperCase() : 'PDF'} • {document.createdAt ? new Date(document.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : 'Unknown Date'}
                        </Typography>
                      </CardContent>
                      
                      <CardActions>
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadDocument(document)}
                          fullWidth
                        >
                          Download
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Pagination */}
              {meta.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={meta.totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}

export default Documents;
